import { SettingsleftNavService } from './settings-left-nav-service';
import { Component, ElementRef, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take, tap } from 'rxjs/operators';

import { Toggles } from '../../../../shared/constants/toggles.constant';
import { ToggleService } from '../../../../shared/services/toggle/toggle.service';
import { ISchool } from '../../../typings/interfaces/school.interface';
import { IUser } from '../../../typings/interfaces/user.interface';
import { SettingsRoutesRelativeUrls } from './../../../constants/settings-routes.constant';
import { SettingsPageData } from './../settings-data-service/settings-data.service';
import { ISidebarItem } from './../../../../../nvps-libraries/design/nv-sidebar-list/nv-sidebar.interface';
import { UrlPathService } from 'Src/ng2/shared/services/url-path-service/url-path.service';
import { PORTAL_TYPES } from 'Src/ng2/shared/typings/interfaces/portal.interface';
import { SharedEventTrackers } from 'Src/ng2/shared/services/mixpanel/event-trackers/shared-tracking.service';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';

const SETTINGS_ROUTE_TO_HUMAN = {
  assessments: 'Assessments',
  'master-program': 'Master Program',
};

const SIDE_NAV_ITEM_TO_TOOLTIP = {};

@Component({
  selector: 'settings-left-nav',
  templateUrl: './settings-left-nav.component.html',
  styleUrls: ['./settings-left-nav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SettingsLeftNavComponent implements OnInit {
  currentUser: IUser;
  currentSchool: ISchool;
  public routeOptions: ISidebarItem[];
  private schoolId: string;
  public selectedSidebarItem: string;

  constructor (
    private toggleService: ToggleService,
    private activatedRoute: ActivatedRoute,
    private settingsDataService: SettingsPageData,
    private router: Router,
    private elementRef: ElementRef,
    private urlPathService: UrlPathService,
    private sharedTrackers: SharedEventTrackers,
    private settingsLeftNavService: SettingsleftNavService,
  ) {
    // empty block
  }

  ngOnInit () {
    this.settingsDataService.getCurrentUser$()
      .pipe(
        tap(currentUser => (this.currentUser = currentUser)),
        take(1),
      ).subscribe();

    this.schoolId = UrlNavigationService.getEntityId(this.activatedRoute.snapshot);
    this.currentSchool = this.activatedRoute.snapshot.data.schoolResource;
    this.routeOptions = this.getRouteOptions(this.currentSchool);
    // dynamically set selected item based on url, otherwise default to first option
    const activeUrl = this.getActiveUrl(this.router.routerState.snapshot.url);
    this.selectedSidebarItem = SETTINGS_ROUTE_TO_HUMAN[activeUrl] || this.routeOptions[0]?.key;
    this.settingsLeftNavService.setSelectedItem({ name: this.selectedSidebarItem, tooltip: SIDE_NAV_ITEM_TO_TOOLTIP[this.selectedSidebarItem] });
    this.toggleV4NewSkinMode();
  }

  /**
   * The settings tabs available for this school + user, sourced from the shared service
   * so the left-nav and the side-nav stay in sync.
   */
  public getRouteOptions (school: ISchool): ISidebarItem[] {
    return this.settingsLeftNavService
      .getAvailableSettingsRouteKeys(school, this.currentUser)
      .map(routeOption => ({ key: routeOption, human: routeOption }));
  }

  changeView (route: string): void {
    const url = this.urlPathService.computeDistrictUrlPath(`school/${this.currentSchool._id}/settings/${SettingsRoutesRelativeUrls[route]}`);
    const redirectRoute = [url];
    this.selectedSidebarItem = route;
    this.settingsLeftNavService.setSelectedItem({ name: this.selectedSidebarItem, tooltip: SIDE_NAV_ITEM_TO_TOOLTIP[this.selectedSidebarItem] });
    this.trackLeftNavClick(route);
    this.router.navigate(redirectRoute);
  }

  public goToMyStudents (): void {
    const url = this.urlPathService.computeDistrictUrlPath(`/school/${this.schoolId}/home`);
    this.router.navigate([url]);
  }

  private toggleV4NewSkinMode () : void {
    const v4Toggle = this.toggleService.getToggleState(Toggles.TOGGLE_V4_NEW_SKIN_MODE);
    if (v4Toggle) {
      this.elementRef.nativeElement.classList.add('v4');
    }
  }

  private trackLeftNavClick (selection: string) {
    const navItem = selection;
    const portal = PORTAL_TYPES.SCHOOL;
    this.sharedTrackers.trackLefthandNavEvent({ navItem, portal });
  }

  private getActiveUrl (url: string): string | undefined {
    if (!url) return;
    const [urlWithoutParams] = url.split('?');
    // we want to exclude '/settings' in the remaining path
    const remainingPath = urlWithoutParams.split('/').slice(5);
    return remainingPath.join('/') || undefined;
  }
}
